import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'

export default function AuthPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  const signInMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) => api.auth.signIn(data),
    onSuccess: async (response) => {
      const { user, token } = response.data.data
      // Map backend user format to backoffice format
      const mappedUser = {
        id: user.userId,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`.trim(),
        role: user.role,
        vendorId: user.tenantType === 'vendor' ? user.tenantId : undefined,
        subscriptionTier: user.subscriptionTier,
      }
      setAuth(mappedUser, token)
      posthog.identify(mappedUser.id, {
        email: mappedUser.email,
        name: mappedUser.name,
        role: mappedUser.role,
        vendor_id: mappedUser.vendorId,
        subscription_tier: mappedUser.subscriptionTier,
      })
      if (mappedUser.vendorId) {
        posthog.group('vendor', mappedUser.vendorId)
      }
      posthog.capture('user_logged_in', { role: mappedUser.role, vendor_id: mappedUser.vendorId })

      // For vendor users, check onboarding status before navigating
      if (mappedUser.vendorId) {
        try {
          const onboardingRes = await api.vendors.getOnboardingStatus(mappedUser.vendorId)
          const ob = onboardingRes.data?.data
          if (!ob?.completed) {
            navigate('/vendor/onboarding')
            return
          }
        } catch {
          // If onboarding check fails, proceed to dashboard
        }
      }
      navigate('/dashboard')
    },
    onError: (err: any) => {
      console.error('Login failed:', err)
      console.error('Response:', err.response)
      console.error('Response data:', err.response?.data)
      const errorMessage = err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'Sign in failed'
      setError(errorMessage)
      posthog.capture('user_login_failed', { error: errorMessage })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    signInMutation.mutate({ email, password })
  }

  return (
    <div className="container" style={{
      maxWidth: '450px',
      margin: '4rem auto',
      padding: '2rem'
    }}>
      <div className="card">
        <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Sign In
        </h1>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            disabled={signInMutation.isPending}
            style={{ width: '100%', marginTop: '1rem' }}
          >
            {signInMutation.isPending ? 'Loading...' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'right', marginTop: '0.75rem' }}>
          <button
            onClick={() => navigate('/auth/forgot-password')}
            style={{
              background: 'transparent',
              color: 'var(--color-gray-500)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Forgot password?
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={() => navigate('/auth/signup')}
            style={{
              background: 'transparent',
              color: 'var(--primary-color)',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Don't have an account? Register Your Business
          </button>
        </div>
      </div>
    </div>
  )
}

